import React from 'react';
import {Field, useForm} from 'react-final-form';
import {FormFieldText} from '@yandex-data-ui/cloud-components';

import {
    findClosestValue,
    getCoreFractionOptions,
    getCoresProps,
    getPlatformCoresVariants,
    getPlatformMemoryVariants,
} from 'components/calculator/ComputeFormFields/utils';
import CalculatorFormLayout from 'components/calculator/CalculatorFormLayout/CalculatorFormLayout';
import {ComputeConfig} from 'components/calculator/models';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import {ComputeFormFieldsValues} from 'components/calculator/ComputeFormFields/ComputeFormFields';
import {FormFieldRangeInputPicker} from 'components/formFields';

interface CPUCoresFieldProps extends WithTranslationProps {
    name: string;
    platforms: ComputeConfig['platforms'];
    osProducts: ComputeConfig['osProducts'];
    values: ComputeFormFieldsValues;
    disabled?: boolean;
}

const CPUCoresField: React.FC<CPUCoresFieldProps> = (props) => {
    const {name, platforms, osProducts, disabled, values, i18nK} = props;
    const {change} = useForm<ComputeFormFieldsValues>();

    const handleCoresChange = React.useCallback(
        (newCores?: number) => {
            if (!newCores) {
                return;
            }

            const {platform, coreFraction, memory} = values;
            const platformData = platforms.find(({id}) => id === platform);
            let newCoreFraction = coreFraction;
            let newMemory = memory;

            if (!platformData) {
                return;
            }

            const currentPlatformCoresVariants = getPlatformCoresVariants(
                platformData,
                coreFraction,
            );
            if (!currentPlatformCoresVariants.includes(newCores)) {
                const coreFractionOptions = getCoreFractionOptions(platforms, platform);
                const currentCoreFraction = coreFraction;
                const suitableCoreFractionOption = coreFractionOptions.find(({value}) => {
                    const localCoreFraction = Number(value);
                    const platformCoresVariants = getPlatformCoresVariants(
                        platformData,
                        localCoreFraction,
                    );

                    return (
                        localCoreFraction !== currentCoreFraction &&
                        platformCoresVariants.includes(newCores)
                    );
                });

                if (suitableCoreFractionOption) {
                    newCoreFraction = Number(suitableCoreFractionOption.value);
                    change(`${name}coreFraction` as keyof ComputeFormFieldsValues, newCoreFraction);
                }
            }

            const currentPlatformMemoryVariants = getPlatformMemoryVariants(
                platformData,
                newCoreFraction,
                newCores,
            );
            if (!currentPlatformMemoryVariants.includes(memory)) {
                newMemory = findClosestValue(currentPlatformMemoryVariants, memory);
                change(`${name}memory` as keyof ComputeFormFieldsValues, newMemory);
            }
        },
        [change, name, platforms, values],
    );

    return (
        <CalculatorFormLayout.Row title={i18nK('field_cores')} note={i18nK('context_cores')}>
            <Field name={`${name}cores`}>
                {(fieldProps) =>
                    disabled ? (
                        <FormFieldText {...fieldProps} size="l" disabled={true} />
                    ) : (
                        <FormFieldRangeInputPicker
                            {...fieldProps}
                            {...getCoresProps({platforms, osProducts}, values)}
                            onUpdate={handleCoresChange}
                            size="l"
                        />
                    )
                }
            </Field>
        </CalculatorFormLayout.Row>
    );
};

export default withTranslation('calculator')(CPUCoresField);
